import { useQuery } from "@tanstack/react-query";
import { Modal } from "antd";
import { useEffect, useState } from "react";

import { ReactComponent as Close } from "../../assets/images/icon/close.svg";
import { getReward, getMemberDataByToken } from "../../service";
import { isLogin } from "../../utils";

const WELCOME_POPUP_DISMISSED_KEY = 'welcomePopupDismissed';
const NEW_MEMBER_COUPON_ID = 34;
const NEW_MEMBER_DAYS_LIMIT = 3;

interface WelcomePopupModalProps {
    onVisibilityChange?: (isVisible: boolean) => void;
}

const WelcomePopupModal = ({ onVisibilityChange }: WelcomePopupModalProps) => {
    const [isOpen, setIsOpen] = useState<boolean>(false);

    const isDismissed = (): boolean => {
        return localStorage.getItem(WELCOME_POPUP_DISMISSED_KEY) === 'true';
    };

    const isWithinDaysFromSignup = (createdDate: string, days: number): boolean => {
        const signupDate = new Date(createdDate);
        const now = new Date();
        const diffTime = now.getTime() - signupDate.getTime();
        const diffDays = diffTime / (1000 * 60 * 60 * 24);
        return diffDays <= days;
    };

    const { data: memberData, isLoading: isMemberLoading } = useQuery({
        queryKey: ['member', 'welcomePopup'],
        queryFn: getMemberDataByToken,
        select: (res) => res.data,
        enabled: isLogin() && !isDismissed(),
    });

    const { data: reward } = useQuery({
        queryKey: ['reward'],
        queryFn: getReward,
        select: (res) => res.data,
        enabled: isLogin() && !isDismissed(),
    });

    const hasValidNewMemberCoupon = (): boolean => {
        if (!reward?.issuedCouponList) return false;
        return reward.issuedCouponList.some(coupon =>
            coupon.couponDto?.id === NEW_MEMBER_COUPON_ID &&
            !coupon.isUsed &&
            !coupon.isDelete &&
            new Date(coupon.expireDate) > new Date()
        );
    };

    const handleDismiss = () => {
        localStorage.setItem(WELCOME_POPUP_DISMISSED_KEY, 'true');
        setIsOpen(false);
    };

    useEffect(() => {
        if (!isMemberLoading && memberData && !isDismissed()) {
            const isNewMember = memberData.createdDate
                ? isWithinDaysFromSignup(memberData.createdDate, NEW_MEMBER_DAYS_LIMIT)
                : true;
            setIsOpen(isNewMember);
        }
    }, [memberData, isMemberLoading]);

    useEffect(() => {
        onVisibilityChange?.(isOpen);
    }, [isOpen, onVisibilityChange]);

    const showCouponMessage = hasValidNewMemberCoupon();

    return (
        <Modal
            open={isOpen}
            centered
            footer={null}
            onCancel={handleDismiss}
            closeIcon={<Close width={24} height={24} stroke="var(--secondarys800)" />}
            title={<div className="sub-title/st3 base/black" style={{ textAlign: 'center' }}>환영합니다</div>}
            styles={{ header: { marginBottom: 16 } }}
        >
            <div className="body/b1 secondary/s800">
                안녕하세요{memberData?.name ? `, ${memberData.name}님` : ''}.<br/>
                파지티브호텔 요가와 함께하는 일상을 시작해보세요.
                {showCouponMessage && (
                  <p>첫 구매를 위한 작은 환영이 준비되어 있습니다.</p>
                )}
            </div>

            <div
                className="sub-title/st2 base/black"
                style={{
                    borderRadius: "var(--radius4)",
                    border: "1px solid var(--baseblack)",
                    padding: "var(--space12) var(--space20)",
                    textAlign: "center",
                    cursor: "pointer",
                    marginTop: 24
                }}
                onClick={handleDismiss}
            >
                시작하기
            </div>
        </Modal>
    );
};

export { WelcomePopupModal };
